import {
  AutoSetupResult,
  AutoSetupResultEntry,
} from 'app/frontend/base/api/event'
import type { EventParticipantSelection } from 'app/frontend/types/custom/RawEventParticipantSelections'

export const applyAutoSetupResultToEvent = (
  event: wowaudit.Event,
  result: AutoSetupResult,
): wowaudit.Event => {
  const updatesByParticipantId = new Map<
    number,
    Record<string, EventParticipantSelection>
  >()

  result.activities.forEach((activityResult) => {
    const activityId = activityResult.activityId.toString()
    activityResult.entries.forEach((entry) => {
      const existing = updatesByParticipantId.get(entry.participantId) ?? {}
      existing[activityId] = {
        selected: entry.selected === true,
        role: entry.role,
        spec_id: entry.specId,
        character_id: entry.characterId,
      }
      updatesByParticipantId.set(entry.participantId, existing)
    })
  })

  return {
    ...event,
    participants: event.participants.map((participant) => {
      const updates = updatesByParticipantId.get(participant.id)
      if (!updates) return participant

      const nextSelections = { ...participant.selections }
      Object.entries(updates).forEach(([activityId, selection]) => {
        nextSelections[activityId] = {
          ...(nextSelections[activityId] ?? {}),
          ...selection,
        }
      })
      return { ...participant, selections: nextSelections }
    }),
  }
}

export const buildAutoSetupResultFromEvent = (
  event: wowaudit.Event,
): AutoSetupResult => ({
  activities: event.activities.map((activity) => ({
    activityId: activity.id,
    entries: event.participants.map((participant): AutoSetupResultEntry => {
      const selection = participant.selections[activity.id.toString()] ?? {}
      return {
        participantId: participant.id,
        characterId:
          selection.character_id ??
          participant.altCharacterId ??
          participant.characterId,
        specId:
          selection.spec_id !== undefined
            ? selection.spec_id
            : participant.specId ?? null,
        role: (selection.role ??
          participant.role) as wowaudit.EventParticipant['role'],
        selected:
          selection.selected ?? participant.effectiveDefaultSelectionStatus,
      }
    }),
  })),
})
